/**
 * El copiloto.
 *
 * La pantalla está construida alrededor de una idea: **enseñar de dónde sale cada
 * cifra**. Por eso cada respuesta viene con sus fuentes desplegables, y por eso
 * cuando no entiende la pregunta **lo dice** en lugar de responder cualquier cosa.
 *
 * Se parece a un chat, pero debajo no hay ningún modelo de lenguaje: son reglas sobre
 * tus propios datos. Eso significa que la misma pregunta da siempre la misma
 * respuesta, y que se puede comprobar.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight, Bug, CircleHelp, Info, Send, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { askCopilot, getCopilotCapabilities } from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { Card, ErrorNote, Loading, Money } from '../components/ui';

interface Message {
  id: string;
  from: 'user' | 'copilot';
  text: string;
  sources?: Array<{ label: string; value: number; detail?: string }>;
  suggestions?: string[];
  understood?: boolean;
}

export function CopilotPage(): React.JSX.Element {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  const capabilities = useQuery({
    queryKey: ['copilot', 'capabilities'],
    queryFn: getCopilotCapabilities,
    staleTime: Infinity,
  });

  const ask = useMutation({
    mutationFn: (text: string) => askCopilot(text),
    onSuccess: (answer, text) => {
      setMessages((current) => [
        ...current,
        { id: crypto.randomUUID(), from: 'user', text },
        {
          id: crypto.randomUUID(),
          from: 'copilot',
          text: answer.answer,
          sources: answer.sources,
          suggestions: answer.suggestions,
          understood: answer.understood,
        },
      ]);
    },
    onError: (error, text) => {
      setMessages((current) => [
        ...current,
        { id: crypto.randomUUID(), from: 'user', text },
        {
          id: crypto.randomUUID(),
          from: 'copilot',
          text:
            error instanceof ApiError
              ? error.message
              : 'No he podido consultar tus datos. Inténtalo otra vez.',
          understood: false,
        },
      ]);
    },
  });

  // La conversación baja sola, como se espera de un chat.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, ask.isPending]);

  const send = (text: string): void => {
    const clean = text.trim();
    if (clean.length < 2 || ask.isPending) return;
    setQuestion('');
    ask.mutate(clean);
  };

  return (
    <div className="flex min-h-[70dvh] flex-col space-y-4 animate-ed-fade-in">
      <header>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Copiloto</h1>
        <p className="mt-0.5 text-sm text-ed-text-muted">
          Pregúntame por tus cifras. Te digo de dónde sale cada número.
        </p>
      </header>

      {/* Cómo funciona. Se dice antes de la primera pregunta, no después. */}
      {messages.length === 0 && (
        <>
          <Card className="!border-ed-cyan/25 !bg-ed-cyan/5">
            <div className="flex items-start gap-3">
              <Info size={18} className="mt-0.5 shrink-0 text-ed-cyan-light" aria-hidden="true" />
              <div className="text-sm text-ed-cyan-light">
                <p className="font-medium">Sin modelos de lenguaje</p>
                <p className="mt-1 text-xs">
                  El copiloto consulta <strong>tus datos en el servidor</strong> y responde con
                  reglas propias. Tus cifras no salen de aquí, la misma pregunta da siempre la
                  misma respuesta, y cada número viene con su fuente. Cuando no entiende algo,
                  lo dice.
                </p>
              </div>
            </div>
          </Card>

          <Card title="Puedes preguntarme">
            {capabilities.isLoading && <Loading />}
            <ul className="grid gap-2 sm:grid-cols-2">
              {(capabilities.data?.examples ?? []).map((example) => (
                <li key={example}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 rounded-ed border border-ed-bg-border/40 px-3 py-2.5 text-left text-sm transition-colors hover:border-ed-gold/50 hover:bg-ed-bg-elevated/50"
                    onClick={() => send(example)}
                  >
                    <span className="min-w-0 truncate">{example}</span>
                    <ArrowRight size={14} className="shrink-0 text-ed-text-muted" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}

      {/* La conversación */}
      {messages.length > 0 && (
        <div className="flex-1 space-y-3">
          {messages.map((message) =>
            message.from === 'user' ? (
              <div key={message.id} className="flex justify-end">
                <p className="max-w-[85%] rounded-ed bg-ed-gold/15 px-3 py-2 text-sm text-ed-gold">
                  {message.text}
                </p>
              </div>
            ) : (
              <div key={message.id} className="flex justify-start">
                <div
                  className={`max-w-[92%] space-y-2 rounded-ed px-3 py-2.5 text-sm ${
                    message.understood === false
                      ? 'border border-ed-coral-deep/40 bg-ed-coral-deep/10 text-ed-coral-light'
                      : 'border border-ed-bg-border/40 bg-ed-bg-elevated/50'
                  }`}
                >
                  <p className="flex items-start gap-2">
                    {message.understood === false ? (
                      <CircleHelp size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
                    ) : (
                      <Sparkles size={15} className="mt-0.5 shrink-0 text-ed-gold" aria-hidden="true" />
                    )}
                    <span>{message.text}</span>
                  </p>

                  {/* Las fuentes: es lo que permite comprobar la respuesta. */}
                  {message.sources && message.sources.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs text-ed-text-muted hover:text-ed-text-silver">
                        Ver de dónde sale ({message.sources.length}{' '}
                        {message.sources.length === 1 ? 'dato' : 'datos'})
                      </summary>
                      <ul className="mt-2 space-y-1 border-t border-ed-bg-border/30 pt-2">
                        {message.sources.map((source, index) => (
                          <li key={`${source.label}-${index}`} className="flex justify-between gap-3 text-xs">
                            <span className="min-w-0 truncate text-ed-text-muted">
                              {source.label}
                              {source.detail && <span className="ml-1 opacity-70">· {source.detail}</span>}
                            </span>
                            <Money amount={source.value} className="shrink-0" />
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {/* Si no entendió, se ofrecen alternativas. */}
                  {message.suggestions && message.suggestions.length > 0 && (
                    <ul className="mt-2 space-y-1 border-t border-ed-coral-deep/30 pt-2">
                      {message.suggestions.slice(0, 4).map((suggestion) => (
                        <li key={suggestion}>
                          <button
                            type="button"
                            className="text-left text-xs underline hover:no-underline"
                            onClick={() => send(suggestion)}
                          >
                            {suggestion}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ),
          )}

          {ask.isPending && (
            <div className="flex justify-start">
              <p className="rounded-ed border border-ed-bg-border/40 bg-ed-bg-elevated/50 px-3 py-2 text-sm text-ed-text-muted">
                Consultando tus datos…
              </p>
            </div>
          )}

          <div ref={endRef} />
        </div>
      )}

      {ask.isError && !messages.some((m) => m.from === 'copilot' && m.understood === false) && (
        <ErrorNote message="No se pudo consultar. Comprueba tu conexión." />
      )}

      {/* La caja de escritura */}
      <form
        className="sticky bottom-20 z-10 sm:bottom-0"
        onSubmit={(event) => {
          event.preventDefault();
          send(question);
        }}
      >
        <div className="flex gap-2 bg-ed-bg-primary/90 py-2 backdrop-blur">
          <input
            className="ed-input flex-1"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="¿Cuánto gasté este mes?"
            aria-label="Escribe tu pregunta"
            autoComplete="off"
          />
          <button
            type="submit"
            className="ed-btn-primary !min-h-[44px] !w-auto !px-4"
            disabled={question.trim().length < 2 || ask.isPending}
            aria-label="Preguntar"
          >
            <Send size={18} aria-hidden="true" />
          </button>
        </div>
      </form>

      {messages.length > 0 && (
        <button
          type="button"
          className="ed-btn-ghost w-full text-xs"
          onClick={() => setMessages([])}
        >
          <Bug size={13} aria-hidden="true" />
          Empezar de nuevo
        </button>
      )}
    </div>
  );
}
